import { doc, setDoc, collection, query, where, getDocs, limit, updateDoc, getDoc as getFirestoreDoc, orderBy } from "firebase/firestore";
import { ref, uploadBytes } from "firebase/storage";
import { db, storage, firestorePathId } from './firebaseConfig';
import { auth, functions, functionsSouthAmerica } from './firebase';
import { httpsCallable } from 'firebase/functions';
import { FirebaseError } from "firebase/app"; // para teste de erros
import { logErrorToService } from '../utils/logService';
import {
    cacheCatalogoContrachequeServidor,
    cacheCompetenciasServidor,
    chaveCacheCatalogoContrachequeServidor,
    chaveCacheCompetenciasServidor,
    normalizarVinculosCompetencias,
} from './competenciasCache';

export const getAcessoServidorByCPF = async (municipioId, cpf) => {
    try {
        const cleanedCpf = cpf.replace(/[.-]/g, '');
        const docPath = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/Servidoresacesso/${cleanedCpf}`;
        const docRef = doc(db, docPath);
        const docSnap = await getFirestoreDoc(docRef);

        if (docSnap.exists()) {
            return { id: docSnap.id, ...docSnap.data() };
        } else {
            console.warn(`[Firebase] Nenhum documento de acesso encontrado para o CPF: ${cleanedCpf}`);
            return null;
        }
    } catch (error) {
        console.error("Erro ao buscar acesso do servidor por CPF:", error);
        throw error;
    }
};

export const getVinculosByCPF = async (municipioId, cpf, uid) => {
    if (!municipioId || !cpf || !uid) return [];
    try {
        const cleanedCpf = cpf.replace(/[.-]/g, '');
        const collectionPath = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/DadosServidores`;
        const q = query(collection(db, collectionPath), where("doc_cpf", "==", cleanedCpf));
        const querySnapshot = await getDocs(q);
        
        if (querySnapshot.empty) {
            console.warn(`[Firebase] Nenhum vínculo de dados encontrado para o CPF: ${cleanedCpf}`);
            return [];
        }
        
        return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), vinculo_id: doc.data().vinculo_id }));
    } catch (error) {
        console.error("Erro ao buscar vínculos por CPF:", error);
        return [];
    }
};

export const getDadosServidorByCPF = async (municipioId, cpf, vinculoId) => {
    try {
        if (!municipioId || !cpf || vinculoId === undefined) {
            console.warn("getDadosServidorByCPF: IDs de município, CPF ou vínculo não fornecidos.");
            return null;
        }
        const cleanedCpf = cpf.replace(/[.-]/g, '');
        const docId = `${cleanedCpf}-${vinculoId}`;
        const docRef = doc(db, `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/DadosServidores`, docId);
        const docSnap = await getFirestoreDoc(docRef);

        if (docSnap.exists()) {
            return docSnap.data();
        } else {
            console.warn(`Nenhum dado de servidor encontrado para o CPF: ${cleanedCpf} e Vínculo: ${vinculoId}`);
            return null;
        }
    } catch (error) {
        console.error("Erro ao buscar dados do servidor por CPF e Vínculo:", error);
        throw error;
    }
};

export const updateServerPassword = async (municipioId, cpf, newPassword) => {
    try {
        const cleanedCpf = cpf.replace(/[.-]/g, '');
        const alterarSenha = httpsCallable(functions, 'alterarSenhaServidor');
        await alterarSenha({ municipioId, cpf: cleanedCpf, newPassword });
    } catch (error) {
        console.error("Erro ao atualizar a senha do servidor:", error);
        throw error;
    }
};

export const updateAcessoWithUID = async (municipioId, cpf, uid) => {
    try {
        const cleanedCpf = cpf.replace(/[.-]/g, '');
        const docPath = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/Servidoresacesso/${cleanedCpf}`;
        const docRef = doc(db, docPath);
        await updateDoc(docRef, { uid });
    } catch (error) {
        console.error("Erro ao atualizar o documento de acesso com o UID:", error);
        throw error;
    }
};

/**
 * Realiza o upload de um arquivo para o Firebase Storage.
 */
export const uploadFile = async (file, path) => {
    try {
        if (!file || !path) {
            throw new Error("Arquivo ou caminho de upload não fornecidos.");
        }
        const storageRef = ref(storage, path);
        
        const metadata = {
            contentType: file.type
        };

        const snapshot = await uploadBytes(storageRef, file, metadata);
        return snapshot.ref.fullPath;
    } catch (error) {
        console.error("Erro detalhado ao fazer upload do arquivo:", error.message, error.stack);
        throw error;
    }
};

export const saveRecadastramento = async (municipioId, data, user) => {
    if (!municipioId || !data || !data.campanhaId || !data.id || !user || !user.uid || !user.cpf) {
        const errorMessage = "Dados insuficientes para salvar. É necessário 'municipioId', 'data', 'data.campanhaId', 'data.id', 'user.uid' e 'user.cpf'.";
        console.error(errorMessage, { data, user });
        throw new Error(errorMessage);
    }

    try {
        const docId = data.id;
        const cleanedUserCpf = user.cpf.replace(/[.-]/g, '');

        const dataToSave = {
            ...data,
            userId: user.uid,
            userCpf: cleanedUserCpf,
        };

        const docPath = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/recadastramentos/${data.campanhaId}/servidores/${docId}`;
        const docRef = doc(db, docPath);

        await setDoc(docRef, dataToSave, { merge: true });
        
        return docId;
    } catch (error) {
        console.error("Erro ao salvar dados do recadastramento no Firestore:", error);
        throw error;
    }
};

export const finalizarRecadastramentoServidorSeguro = async ({ municipioId, campanhaId, recadastramentoId, confirmacaoVeracidade }) => {
    if (!municipioId || !campanhaId || !recadastramentoId || confirmacaoVeracidade !== true) {
        throw new Error('Dados insuficientes para finalizar o recadastramento com segurança.');
    }
    const callable = httpsCallable(functions, 'finalizarRecadastramentoServidorSeguro');
    const response = await callable({ municipioId, campanhaId, recadastramentoId, confirmacaoVeracidade: true });
    if (!response?.data?.success) throw new Error('A finalização segura do recadastramento não foi confirmada.');
    return response.data;
};

export const getRecadastramentoByCPF = async (municipioId, cpf) => {
    try {
        if (!cpf || !municipioId) {
            console.warn("getRecadastramentoByCPF: cpf ou municipioId inválido/ausente.");
            return null;
        }
        
        const cleanedCpf = cpf.replace(/[.-]/g, '');

        const activeCampaign = await getActiveCampaignRequiredFields(municipioId);
        if (!activeCampaign) {
            console.warn(`Nenhuma campanha ativa encontrada para o município ${municipioId}.`);
            return null;
        }
        
        const collectionPath = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/recadastramentos/${activeCampaign.id}/servidores`;
        
        const q = query(collection(db, collectionPath), where("userCpf", "==", cleanedCpf), limit(1));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            const docSnap = querySnapshot.docs[0];
            return { id: docSnap.id, ...docSnap.data() };
        } else {
            return null;
        }
    } catch (error) {
        console.error("Erro ao buscar recadastramento por CPF na campanha ativa:", error);
        throw error;
    }
};

export const getCampaignById = async (municipioId, campanhaId) => {
    try {
        const safeMunicipioId = String(municipioId || '').trim();
        const safeCampanhaId = String(campanhaId || '').trim();
        if (!safeMunicipioId || !safeCampanhaId) return null;

        const campaignRef = doc(
            db,
            `artifacts/${firestorePathId}/public/data/recadastramentoCampaigns/${safeCampanhaId}`
        );
        const campaignSnapshot = await getFirestoreDoc(campaignRef);
        if (!campaignSnapshot.exists()) return null;

        const campaignData = campaignSnapshot.data() || {};
        if (String(campaignData.municipioId || '').trim() !== safeMunicipioId) {
            return null;
        }

        return { id: campaignSnapshot.id, ...campaignData };
    } catch (error) {
        console.error('Erro ao buscar campanha de recadastramento por ID:', error);
        throw error;
    }
};

export const getActiveCampaignRequiredFields = async (municipioId) => {
    try {
        const campaignsCollectionRef = collection(db, `artifacts/${firestorePathId}/public/data/recadastramentoCampaigns`);
        const q = query(
            campaignsCollectionRef,
            where('status', '==', 'ativa'),
            where('municipioId', '==', municipioId),
            limit(1)
        );
        
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
            return { id: querySnapshot.docs[0].id, ...querySnapshot.docs[0].data() };
        } else {
            return null;
        }
    } catch (error) {
        console.error("ERRO CRÍTICO ao buscar campanha ativa:", error);
        throw error;
    }
};

export const updateRecadastramentoStatus = async (municipioId, campanhaId, cpf, newStatus) => {
    try {
        if (!municipioId || !campanhaId || !cpf || !newStatus) {
            throw new Error("municipioId, campanhaId, cpf e newStatus devem ser fornecidos.");
        }
        const cleanedCpf = cpf.replace(/[.-]/g, '');
        const collectionPath = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/recadastramentos/${campanhaId}/servidores`;
        
        const q = query(collection(db, collectionPath), where("userCpf", "==", cleanedCpf), limit(1));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            throw new Error(`Nenhum recadastramento encontrado para o CPF ${cleanedCpf} na campanha ${campanhaId}.`);
        }
        
        const docRef = querySnapshot.docs[0].ref;

        await updateDoc(docRef, {
            status: newStatus,
            lastUpdate: new Date(),
        });
    } catch (error) {
        console.error("Erro ao atualizar o status do recadastramento:", error);
        throw error;
    }
};

export const getMunicipioById = async (municipioId) => {
    try {
        if (!municipioId) {
            console.warn("getMunicipioById: municipioId não fornecido.");
            return null;
        }
        const docRef = doc(db, `artifacts/${firestorePathId}/public/data/municipalities`, municipioId);
        const docSnap = await getFirestoreDoc(docRef);

        if (docSnap.exists()) {
            return docSnap.data();
        } else {
            console.warn("Nenhum documento de município encontrado para o ID:", municipioId);
            return null;
        }
    } catch (error) {
        console.error("Erro ao buscar dados do município:", error);
        throw error;
    }
};

export const getRhUserById = async (rhUserId) => {
    try {
        if (!rhUserId) {
            console.warn("getRhUserById: rhUserId não fornecido.");
            return null;
        }
        const docRef = doc(db, `artifacts/${firestorePathId}/public/data/rhUsers`, rhUserId);
        const docSnap = await getFirestoreDoc(docRef);

        if (docSnap.exists()) {
            return docSnap.data();
        } else {
            console.warn("Nenhum usuário DP encontrado para o ID:", rhUserId);
            return null;
        }
    } catch (error) {
        console.error("Erro ao buscar dados do usuário DP:", error);
        throw error;
    }
};

// ===== INÍCIO DA FUNÇÃO DE CAMPANHAS DE RECADASTRAMENTO =====
export const getCampaignsByMunicipio = async (municipioId) => {
    try {
        const campaignsCollectionRef = collection(db, `artifacts/${firestorePathId}/public/data/recadastramentoCampaigns`);
        const q = query(
            campaignsCollectionRef,
            where('municipioId', '==', municipioId)
        );
        
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
            return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } else {
            return [];
        }
    } catch (error) {
        console.error("Erro ao buscar campanhas por município:", error);
        throw error;
    }
};
// ===== FIM DA FUNÇÃO DE CAMPANHAS DE RECADASTRAMENTO =====

// ===== INÍCIO DA FUNÇÃO DE HISTÓRICO DE RECADASTRAMENTO =====
export const getHistoricoRecadastramento = async (municipioId, cpf) => {
    try {
        const campaigns = await getCampaignsByMunicipio(municipioId);
        if (!campaigns || campaigns.length === 0) {
            return [];
        }

        const cleanedCpf = cpf.replace(/[.-]/g, '');
        
        const historicoPromises = campaigns.map(async (campaign) => {
            if (!campaign.id) return null;
            
            const collectionPath = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/recadastramentos/${campaign.id}/servidores`;
            
            const q = query(
                collection(db, collectionPath), 
                where("userCpf", "==", cleanedCpf), 
                where("status", "==", "aceito"), 
                limit(1)
            );
            
            const querySnapshot = await getDocs(q);
            
            if (!querySnapshot.empty) {
                const docSnap = querySnapshot.docs[0];
                return { 
                    ...docSnap.data(), 
                    id: docSnap.id,
                    campanhaId: campaign.id,
                    campanhaNome: campaign.name || `Campanha (ID: ${campaign.id})`
                };
            }
            
            return null;
        });

        const historicoResultados = await Promise.all(historicoPromises);
        const historicoFinal = historicoResultados.filter(h => h !== null);
        return historicoFinal;

    } catch (error) {
        console.error("Erro ao buscar histórico de recadastramento:", error);
        throw error;
    }
};
// ===== FIM DA FUNÇÃO DE HISTÓRICO DE RECADASTRAMENTO =====

// ===== INÍCIO DA NOVA FUNÇÃO (AVISOS) =====
const avisosServidorCache = new Map();
const avisosServidorInFlight = new Map();
const AVISOS_SERVIDOR_CACHE_MS = 5 * 60 * 1000;

/**
 * Busca avisos autorizados pelo backend para a sessão atual.
 * Cache e requisições concorrentes são isolados por usuário e município.
 */
export const getAvisosAtivos = async (municipioId, { forceRefresh = false } = {}) => {
    if (!municipioId) {
        console.warn("getAvisosAtivos: municipioId não fornecido.");
        return [];
    }

    const uid = String(auth.currentUser?.uid || '').trim();
    if (!uid) throw new Error('Sessão do servidor indisponível para consultar avisos.');
    const cacheKey = `${uid}|${String(municipioId)}`;
    const cached = avisosServidorCache.get(cacheKey);
    if (!forceRefresh && cached && Date.now() - cached.at < AVISOS_SERVIDOR_CACHE_MS) {
        return cached.items;
    }

    const emAndamento = avisosServidorInFlight.get(cacheKey);
    if (emAndamento) return emAndamento;

    const promessa = (async () => {
        const listarAvisosServidor = httpsCallable(functions, 'listarAvisosServidor');
        const response = await listarAvisosServidor({ municipioId });
        const avisos = Array.isArray(response?.data?.avisos) ? response.data.avisos : [];
        // Uma resposta tardia de sessão encerrada não entra no cache de outra sessão.
        if (String(auth.currentUser?.uid || '').trim() !== uid) {
            throw new Error('Sessão alterada durante a consulta de avisos.');
        }
        avisosServidorCache.set(cacheKey, { at: Date.now(), items: avisos });
        return avisos;
    })();
    avisosServidorInFlight.set(cacheKey, promessa);
    try {
        return await promessa;
    } catch (error) {
        console.error("Erro ao buscar avisos ativos:", error);
        throw error;
    } finally {
        if (avisosServidorInFlight.get(cacheKey) === promessa) {
            avisosServidorInFlight.delete(cacheKey);
        }
    }
};
// ===== FIM DA NOVA FUNÇÃO (AVISOS) =====


// ---------------------------
// Funções auxiliares para CONTRACHEQUE (ATUALIZADAS COM FALLBACK)
// ---------------------------

/**
 * Faz fetch de um JSON público hospedado em /artifacts/...
 * Retorna null se não existir ou houver erro.
 */
async function fetchPublicArtifactJson(path) {
  try {
    if (!path) return null;
    const base = '/'; // ajuste se seu hosting usa prefixo
    const res = await fetch(base + path, { cache: 'no-store' });
    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    console.warn("fetchPublicArtifactJson erro:", err);
    return null;
  }
}

/**
 * getCompetenciasVisiveis: tenta a query ideal; se falhar, tenta listar sem filtro; se falhar, tenta arquivo público
 */
export const getCompetenciasVisiveis = async (municipioId) => {
    try {
        if (!municipioId) {
            console.warn("getCompetenciasVisiveis: municipioId não fornecido.");
            return [];
        }

        const collectionPath = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/contracheque`;
        const collectionRef = collection(db, collectionPath);

        // 1) tentativa ideal: query por visivelParaServidor + orderBy
        try {
            const q = query(collectionRef, where('visivelParaServidor', '==', true), orderBy('competencia', 'desc'));
            const snap = await getDocs(q);

            if (!snap.empty) {
                const comps = snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) }));
                comps.sort((a,b) => (b.id||'').localeCompare(a.id||''));
                return comps;
            }
            return [];
        } catch (errQuery) {
            console.warn("Query por competências falhou (vai tentar fallback):", errQuery);

            // 2) tentativa alternativa: listar a coleção inteira e filtrar cliente-side
            try {
                const snapAll = await getDocs(collectionRef);
                if (!snapAll.empty) {
                    const all = snapAll.docs.map(d => ({ id: d.id, ...(d.data() || {}) }));
                    const comps = all.filter(x => x && x.visivelParaServidor === true)
                                 .sort((a,b) => (b.id||'').localeCompare(a.id||''));
                    return comps;
                }
                return [];
            } catch (errList) {
                console.warn("Tentativa de listar coleção sem filtro também falhou:", errList);
                // 3) fallback final: tentar arquivo público (se existir)
                const path = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/contracheque/competencias.json`;
                const json = await fetchPublicArtifactJson(path);
                if (json && Array.isArray(json)) {
                    const comps = json.map(item => ({ id: item.id || item.competencia || '', label: item.label || item.id || item.competencia }));
                    comps.sort((a,b) => (b.id||'').localeCompare(a.id||''));
                    return comps;
                }
                if (errQuery instanceof FirebaseError) throw errQuery;
                throw errList;
            }
        }
    } catch (finalErr) {
        console.error("getCompetenciasVisiveis erro definitivo:", finalErr);
        throw finalErr;
    }
};


const cleanCpfContrachequeServidor = (value) => String(value || '').replace(/\D/g, '');
const cleanMatriculaContrachequeServidor = (value) => String(value || '').trim();
const CATALOGO_CONTRACHEQUE_ANOS_RETROATIVOS = 6;

const isUnauthenticatedCallableError = (error) => (
    String(error?.code || '').toLowerCase().includes('unauthenticated') ||
    String(error?.message || '').toLowerCase().includes('usuário autenticado')
);

const montarAnosCatalogoContracheque = () => {
    const anoAtual = new Date().getFullYear();
    return Array.from({ length: CATALOGO_CONTRACHEQUE_ANOS_RETROATIVOS }, (_, index) => String(anoAtual - index));
};

const normalizarCompetenciasCatalogo = (catalogos = []) => {
    const porCompetencia = new Map();

    catalogos.forEach((catalogo) => {
        const competencias = Array.isArray(catalogo?.competencias) ? catalogo.competencias : [];
        competencias.forEach((competencia) => {
            const id = String(competencia?.id || '').trim();
            if (!/^\d{4}-\d{2}$/.test(id)) return;

            const folhas = (Array.isArray(competencia?.folhas) ? competencia.folhas : [])
                .map((folha) => ({
                    id: String(folha?.id || 'folha_principal').trim() || 'folha_principal',
                    descricao: String(folha?.descricao || 'Folha principal').trim() || 'Folha principal',
                    liberacaoFiltrada: folha?.liberacaoFiltrada === true,
                }))
                .filter((folha) => folha.id);

            if (folhas.length === 0) return;
            porCompetencia.set(id, {
                id,
                label: `${id.slice(5, 7)}/${id.slice(0, 4)}`,
                folhas,
                origem: 'catalogo_firestore',
            });
        });
    });

    return [...porCompetencia.values()].sort((a, b) => b.id.localeCompare(a.id));
};

const getCompetenciasCatalogoServidorDireto = async (municipioId, authenticatedUser) => {
    const cacheKey = chaveCacheCatalogoContrachequeServidor({
        uid: authenticatedUser.uid,
        municipioId,
    });

    return cacheCatalogoContrachequeServidor.run(cacheKey, async () => {
        const basePath = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/contrachequeCatalogoServidor`;
        const anos = montarAnosCatalogoContracheque();
        const resultados = await Promise.all(anos.map(async (ano) => {
            try {
                const snap = await getFirestoreDoc(doc(db, basePath, ano));
                return snap.exists() ? { ano, ...(snap.data() || {}) } : null;
            } catch (error) {
                const code = String(error?.code || '').toLowerCase();
                if (code.includes('not-found')) return null;
                throw error;
            }
        }));

        return normalizarCompetenciasCatalogo(resultados.filter(Boolean));
    });
};

/**
 * Lista os períodos liberados usando primeiro o catálogo seguro do Firestore.
 * A Function antiga permanece somente como fallback durante a migração.
 */
export const getCompetenciasVisiveisServidor = async (municipioId, cpf, vinculos = []) => {
    const cleanedCpf = cleanCpfContrachequeServidor(cpf);
    const vinculosNormalizados = normalizarVinculosCompetencias(vinculos);

    if (!municipioId || !cleanedCpf || vinculosNormalizados.length === 0) {
        return [];
    }

    const authenticatedUser = auth.currentUser;
    if (!authenticatedUser) {
        return [];
    }

    try {
        await authenticatedUser.getIdToken();
        if (auth.currentUser?.uid !== authenticatedUser.uid) return [];

        const competenciasDiretas = await getCompetenciasCatalogoServidorDireto(municipioId, authenticatedUser);
        // Catálogo genérico não conhece o CPF do servidor. Havendo liberação
        // filtrada, consultar o fallback autenticado para não exibir meses de terceiros.
        const contemFiltrada = competenciasDiretas.some((competencia) =>
            (competencia.folhas || []).some((folha) => folha.liberacaoFiltrada === true)
        );
        if (competenciasDiretas.length > 0 && !contemFiltrada) return competenciasDiretas;
    } catch (error) {
        if (!auth.currentUser) return [];
        console.warn('Catálogo direto de contracheques indisponível; usando fallback temporário.', error);
    }

    try {
        const cacheKey = chaveCacheCompetenciasServidor({
            uid: authenticatedUser.uid,
            municipioId,
            cpf: cleanedCpf,
            vinculos: vinculosNormalizados,
        });
        return await cacheCompetenciasServidor.run(cacheKey, async () => {
            const callable = httpsCallable(functionsSouthAmerica, 'listarCompetenciasContrachequeServidor');
            const response = await callable({
                municipioId,
                cpf: cleanedCpf,
                vinculos: vinculosNormalizados,
            });
            const competencias = response?.data?.competencias;
            if (!Array.isArray(competencias)) throw new Error('Resposta inválida ao listar competências.');
            return [...competencias].sort((a, b) => String(b.id || '').localeCompare(String(a.id || '')));
        });
    } catch (error) {
        if (!auth.currentUser || isUnauthenticatedCallableError(error)) return [];

        logErrorToService('error', 'portal-servidor', error, null, 'CLOUD_FUNCTION_ERROR', null, {
            source: 'httpsCallable',
            functionName: 'listarCompetenciasContrachequeServidor',
            operation: 'list_visible_payslip_periods_fallback',
            module: 'contracheques',
        });
        console.error('Não foi possível listar competências nem pelo catálogo nem pelo fallback:', error);
        return [];
    }
};

const normalizarContrachequeDireto = (docSnap) => {
    const data = docSnap?.data?.() || {};
    return {
        ...data,
        id: docSnap.id,
        rubricas: Array.isArray(data.rubricas) ? data.rubricas : [],
    };
};

const matriculaDoContracheque = (item) => cleanMatriculaContrachequeServidor(
    item?.matriculaNormalizada || item?.matricula || item?.vinc_matricula || item?.vinculo_matricula
);

const cpfDoContracheque = (item) => cleanCpfContrachequeServidor(
    item?.cpfNormalizado || item?.cpf || item?.doc_cpf || item?.func_cpf || item?.servidor_cpf
);

const normalizarFolhaDescricaoContracheque = (value) => {
    const description = String(value || '').replace(/\s+/g, ' ').trim() || 'Folha principal';
    const id = description
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase('pt-BR')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 80) || 'folha_principal';
    return { id, description };
};

const folhaDocumentoCompativel = ({ data, folhaId, competenciaLegada = false }) => {
    if (!data) return false;
    if (competenciaLegada === true) return true;

    const folhaDocumento = String(
        data.folhaId ||
        normalizarFolhaDescricaoContracheque(data.folhaDescricao || data.descricao_folha).id
    ).trim();

    return folhaDocumento === String(folhaId || 'folha_principal').trim();
};

const pontuarContrachequeDireto = (item) => {
    const rubricas = Array.isArray(item?.rubricas) ? item.rubricas : [];
    let pontos = 0;

    if (rubricas.length > 0) pontos += 60;
    if (String(item?.docIdFinalContracheque || '') === String(item?.id || '')) pontos += 40;
    if (item?.cpfNormalizado) pontos += 12;
    if (item?.matriculaNormalizada) pontos += 10;
    if (item?.folhaId) pontos += 10;
    if (item?.servidorDocId) pontos += 6;
    if (item?.totalProventos !== undefined || item?.totalDescontos !== undefined || item?.liquido !== undefined || item?.valorLiquido !== undefined) pontos += 12;
    if (item?.origem) pontos += 3;

    return pontos;
};

const timestampContrachequeDireto = (item) => {
    const value = item?.atualizadoEm || item?.updatedAt || null;
    if (typeof value?.toMillis === 'function') return value.toMillis();
    if (Number.isFinite(Number(value?.seconds))) return Number(value.seconds) * 1000;
    const parsed = Date.parse(String(value || ''));
    return Number.isFinite(parsed) ? parsed : 0;
};

const escolherContrachequeCanonico = (atual, candidato) => {
    if (!atual) return candidato;
    const scoreAtual = pontuarContrachequeDireto(atual);
    const scoreCandidato = pontuarContrachequeDireto(candidato);
    if (scoreCandidato !== scoreAtual) return scoreCandidato > scoreAtual ? candidato : atual;

    const dataAtual = timestampContrachequeDireto(atual);
    const dataCandidato = timestampContrachequeDireto(candidato);
    if (dataCandidato !== dataAtual) return dataCandidato > dataAtual ? candidato : atual;

    return String(candidato?.id || '').localeCompare(String(atual?.id || '')) > 0 ? candidato : atual;
};

const consolidarContrachequesDiretos = ({ itens = [], cpf, folhaId, competenciaLegada = false }) => {
    const porIdentidade = new Map();

    itens.forEach((item) => {
        if (!item || cpfDoContracheque(item) !== cpf) return;
        if (!folhaDocumentoCompativel({ data: item, folhaId, competenciaLegada })) return;

        const matricula = matriculaDoContracheque(item);
        const identidadeVinculo = matricula || String(item.servidorDocId || item.servidorVinculadoId || item.id || '').trim();
        if (!identidadeVinculo) return;

        // Em competência antiga a liberação era do mês inteiro. Documentos antigos
        // podem coexistir com IDs diferentes para o mesmo CPF/matrícula; tratamos
        // todos como a mesma folha lógica e escolhemos a cópia mais completa.
        const identidadeFolha = competenciaLegada
            ? 'folha_principal'
            : String(item.folhaId || normalizarFolhaDescricaoContracheque(item.folhaDescricao || item.descricao_folha).id || folhaId).trim();

        const chave = `${identidadeVinculo}::${identidadeFolha}`;
        porIdentidade.set(chave, escolherContrachequeCanonico(porIdentidade.get(chave), item));
    });

    return [...porIdentidade.values()].sort((a, b) => (
        matriculaDoContracheque(a).localeCompare(matriculaDoContracheque(b), 'pt-BR', { numeric: true })
    ));
};

const consultarContrachequesPorCampoCpf = async ({
    collectionRef,
    campoCpf,
    cpf,
    folhaId,
    competenciaLegada = false,
}) => {
    const restricoes = [where(campoCpf, '==', cpf)];

    // Nas competências atuais, restringir a query à folha selecionada é parte
    // da segurança: uma folha oculta não deve sequer entrar no resultado.
    if (!competenciaLegada) {
        restricoes.push(where('folhaId', '==', String(folhaId || '').trim()));
    }

    const snap = await getDocs(query(collectionRef, ...restricoes));
    return snap.docs.map(normalizarContrachequeDireto);
};

/**
 * Leitura direta pelo contrato REAL já gravado pelo Master:
 * - não depende do formato do ID do documento;
 * - consulta o CPF dentro da competência;
 * - aceita registros novos (cpfNormalizado) e antigos (cpf/doc_cpf);
 * - em competência legada retorna todos os documentos do CPF e consolida
 *   duplicidades históricas pelo vínculo/matrícula;
 * - em competência atual restringe a consulta à folha liberada selecionada.
 *
 * Nenhum contracheque é regravado ou migrado.
 */
const getContrachequesServidorDireto = async ({
    municipioId,
    competencia,
    cpf,
    folhaId,
    vinculos = [],
    competenciaLegada = false,
}) => {
    const collectionPath = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/contracheque/${competencia}/servidores`;
    const collectionRef = collection(db, collectionPath);
    // Ordem otimizada para o contrato atual do Master e, depois, aliases históricos.
    // Assim o fluxo normal costuma resolver em uma única query e só consulta aliases
    // adicionais quando ainda faltam vínculos esperados.
    const camposCpf = ['cpf', 'cpfNormalizado', 'doc_cpf', 'func_cpf', 'servidor_cpf'];
    const porDocumento = new Map();
    let houveNegacaoPermissao = false;
    const matriculasEsperadas = new Set(
        (Array.isArray(vinculos) ? vinculos : [])
            .map((vinculo) => String(vinculo?.matricula || '').trim())
            .filter(Boolean)
    );

    for (const campoCpf of camposCpf) {
        try {
            const itens = await consultarContrachequesPorCampoCpf({
                collectionRef,
                campoCpf,
                cpf,
                folhaId,
                competenciaLegada,
            });
            itens.forEach((item) => {
                if (item?.id) porDocumento.set(item.id, item);
            });

            if (porDocumento.size > 0) {
                const consolidados = consolidarContrachequesDiretos({
                    itens: [...porDocumento.values()],
                    cpf,
                    folhaId,
                    competenciaLegada,
                });
                const matriculasEncontradas = new Set(consolidados.map(matriculaDoContracheque).filter(Boolean));
                const cobriuTodos = matriculasEsperadas.size > 0 &&
                    [...matriculasEsperadas].every((matricula) => matriculasEncontradas.has(matricula));

                // Sem vínculos esperados, a primeira família de CPF que devolver dados é
                // suficiente. Com vínculos, só encerramos quando todos estiverem cobertos.
                if (matriculasEsperadas.size === 0 || cobriuTodos) return consolidados;
            }
        } catch (error) {
            const code = String(error?.code || '').toLowerCase();

            // Uma consulta incompatível com um alias histórico não invalida as demais.
            if (code.includes('permission-denied') || code.includes('failed-precondition') || code.includes('not-found')) {
                console.warn('Campo legado de CPF indisponível na leitura direta do contracheque.', { campoCpf, code });
                if (code.includes('permission-denied')) houveNegacaoPermissao = true;
                continue;
            }
            throw error;
        }
    }

    // Uma consulta negada pelas Rules pode indicar folha liberada somente
    // para determinados vínculos. Nunca confundir a negativa com folha vazia.
    // O fallback autenticado verifica os grants com Admin SDK.
    if (houveNegacaoPermissao) {
        const erro = new Error('Consulta direta negada pelas regras de publicação.');
        erro.code = 'permission-denied';
        throw erro;
    }
    return consolidarContrachequesDiretos({
        itens: [...porDocumento.values()],
        cpf,
        folhaId,
        competenciaLegada,
    });
};

/**
 * Fallback legado autenticado. Mantido durante a migração para cobrir registros
 * antigos ou competências ainda sem os metadados necessários à leitura direta.
 */
export const getContrachequeServidor = async (municipioId, competencia, cpf, matricula, folhaId = 'folha_principal') => {
    if (!municipioId || !competencia || !cpf || !matricula || !folhaId) {
        console.warn("getContrachequeServidor: argumentos insuficientes.", { municipioId, competencia, cpf, matricula, folhaId });
        return null;
    }

    let response;
    try {
        const callable = httpsCallable(functionsSouthAmerica, 'buscarContrachequeServidor');
        response = await callable({
            municipioId,
            competencia,
            cpf: cleanCpfContrachequeServidor(cpf),
            matricula: String(matricula).trim(),
            folhaId: String(folhaId).trim(),
        });
    } catch (error) {
        logErrorToService('error', 'portal-servidor', error, null, 'CLOUD_FUNCTION_ERROR', null, {
            source: 'httpsCallable',
            functionName: 'buscarContrachequeServidor',
            operation: 'get_payslip',
            module: 'contracheques',
        });
        throw error;
    }
    const contracheque = response?.data?.contracheque;

    if (!contracheque) return null;

    return {
        ...contracheque,
        rubricas: Array.isArray(contracheque.rubricas) ? contracheque.rubricas : [],
    };
};


/**
 * Caminho novo do Portal Servidor.
 * 1) a leitura direta é a fonte da verdade quando conclui sem erro técnico;
 * 2) vínculo cadastral ausente no resultado significa apenas que não houve pagamento nele naquela competência/folha;
 * 3) buscarContrachequeServidor só é usado quando a leitura direta falha tecnicamente;
 * 4) nenhuma informação financeira é persistida em cache local.
 */
export const getContrachequesServidor = async (municipioId, competencia, cpf, vinculos = [], folhaId = 'folha_principal') => {
    const cleanedCpf = cleanCpfContrachequeServidor(cpf);
    const folhaIdSegura = String(folhaId || 'folha_principal').trim();
    const vinculosNormalizados = normalizarVinculosCompetencias(vinculos);

    if (!municipioId || !/^\d{4}-\d{2}$/.test(String(competencia || '')) || cleanedCpf.length !== 11 || !folhaIdSegura) {
        return [];
    }

    const authenticatedUser = auth.currentUser;
    if (!authenticatedUser) return [];

    let diretos = [];
    let consultaDiretaConcluida = false;
    let erroLeituraDireta = null;

    try {
        await authenticatedUser.getIdToken();
        if (auth.currentUser?.uid !== authenticatedUser.uid) return [];

        const competenciasCatalogo = await getCompetenciasCatalogoServidorDireto(municipioId, authenticatedUser);
        const competenciaCatalogo = competenciasCatalogo.find((item) => item.id === String(competencia).trim()) || null;
        const folhasCatalogo = Array.isArray(competenciaCatalogo?.folhas) ? competenciaCatalogo.folhas : [];
        const folhaCatalogo = folhasCatalogo.find((item) => String(item?.id || '') === folhaIdSegura) || null;
        const competenciaLegada = folhaIdSegura === 'folha_principal' &&
            folhasCatalogo.length === 1 &&
            String(folhaCatalogo?.descricao || '').trim().toLowerCase() === 'folha principal';

        diretos = await getContrachequesServidorDireto({
            municipioId,
            competencia: String(competencia).trim(),
            cpf: cleanedCpf,
            folhaId: folhaIdSegura,
            vinculos: vinculosNormalizados,
            competenciaLegada,
        });
        consultaDiretaConcluida = true;
    } catch (error) {
        erroLeituraDireta = error;
        console.warn('Leitura direta do contracheque falhou tecnicamente; acionando fallback de contingência.', error);
    }

    // REGRA DE PROTEÇÃO: uma consulta Firestore concluída, inclusive com zero resultados,
    // é definitiva para aquela competência/folha. Não usar Function para "procurar" vínculo
    // cadastral que simplesmente não teve pagamento no mês.
    if (consultaDiretaConcluida) {
        if (vinculosNormalizados.length === 0) return diretos;

        const matriculasPermitidas = new Set(vinculosNormalizados.map((item) => item.matricula).filter(Boolean));
        return diretos.filter((item) => matriculasPermitidas.has(matriculaDoContracheque(item)));
    }

    // A partir daqui houve erro técnico real na leitura direta. O fluxo legado fica apenas
    // como contingência para manter o contracheque disponível enquanto a falha é investigada.
    const vinculosFallbackBase = (Array.isArray(vinculos) ? vinculos : [])
        .map((vinculo) => ({
            matricula: String(
                vinculo?.matricula || vinculo?.vinc_matricula || vinculo?.matriculaServidor ||
                vinculo?.vinculo_matricula || vinculo?.v_matricula || ''
            ).trim(),
        }))
        .filter((vinculo) => vinculo.matricula);

    const fallbackVinculos = vinculosNormalizados.length > 0
        ? vinculosNormalizados
        : vinculosFallbackBase;

    if (fallbackVinculos.length === 0) {
        if (erroLeituraDireta) {
            console.warn('Leitura direta falhou e não há vínculo com matrícula para fallback.', erroLeituraDireta);
        }
        return [];
    }

    const fallbackResultados = await Promise.all(fallbackVinculos.map(async (vinculo) => {
        try {
            return await getContrachequeServidor(
                municipioId,
                competencia,
                cleanedCpf,
                vinculo.matricula,
                folhaIdSegura
            );
        } catch (error) {
            console.warn('Fallback técnico de contracheque falhou para um vínculo.', { matricula: vinculo.matricula, error });
            return null;
        }
    }));

    const porMatricula = new Map();
    fallbackResultados.filter(Boolean).forEach((item) => {
        const matricula = matriculaDoContracheque(item);
        if (matricula && !porMatricula.has(matricula)) porMatricula.set(matricula, item);
    });

    return [...porMatricula.values()];
};
